import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/feature/LanguageSelector";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(href);
    setMobileOpen(false);
  };

  const menuItems = [
    { key: "home", label: t("nav.home"), href: "/", children: [] },
    {
      key: "about",
      label: t("nav.about"),
      href: "/about/introduction",
      children: [
        { label: t("nav.about.intro"), href: "/about/introduction" },
        { label: t("nav.about.org"), href: "/about/organization" },
        { label: t("nav.about.inquiry"), href: "/about/inquiry" },
      ],
    },
    { key: "research", label: t("nav.research"), href: "/research", children: [] },
    {
      key: "partner",
      label: t("nav.partners"),
      href: "/partners/huaqiao",
      children: [
        { label: t("nav.partners.huaqiao"), href: "/partners/huaqiao" },
      ],
    },
    {
      key: "news",
      label: t("nav.news"),
      href: "/news/articles",
      children: [
        { label: t("nav.news.articles"), href: "/news/articles" },
        { label: t("nav.news.brand"), href: "/news/brand" },
        { label: t("nav.news.conference"), href: "/news/conference" },
      ],
    },
    {
      key: "ranking",
      label: t("nav.rankings"),
      href: "/rankings/2025-top100",
      children: [
        { label: t("nav.rankings.top100"), href: "/rankings/2025-top100" },
      ],
    },
    { key: "contact", label: t("nav.contact"), href: "/contact", children: [] },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200/70 shadow-[0_1px_0_0_rgba(0,0,0,0.03)]"
          : "bg-transparent"
      }`}
    >
      {/* Announcement bar */}
      <div className="w-full bg-primary-800 text-background-50 text-xs md:text-sm py-2.5 px-4">
        <div className="w-full flex items-center justify-center gap-2 tracking-wider">
          <i className="ri-star-line w-4 h-4 flex items-center justify-center text-accent-400"></i>
          <span className="font-medium">{t("nav.announcement")}</span>
          <i className="ri-star-line w-4 h-4 flex items-center justify-center text-accent-400"></i>
        </div>
      </div>

      {/* Navbar */}
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" onClick={handleNavClick("/")} className="flex items-center gap-3 cursor-pointer whitespace-nowrap">
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-700 text-background-50">
              <span className="font-heading font-bold text-lg md:text-xl">IABS</span>
            </div>
            <div className="hidden sm:block">
              <div
                className={`font-heading font-bold text-base md:text-lg leading-tight ${
                  scrolled ? "text-foreground-900" : "text-background-50"
                }`}
              >
                {t("footer.brand.name")}
              </div>
              <div
                className={`text-[10px] md:text-xs tracking-widest ${
                  scrolled ? "text-foreground-600" : "text-background-100/80"
                }`}
              >
                {t("footer.brand.subtitle")}
              </div>
            </div>
          </a>

          {/* Desktop menu */}
          <nav className="hidden lg:flex items-center gap-1">
            {menuItems.map((item) => (
              <div
                key={item.key}
                className="relative group"
                onMouseEnter={() => setOpenKey(item.key)}
                onMouseLeave={() => setOpenKey(null)}
              >
                <a
                  href={item.href}
                  onClick={item.children.length === 0 ? handleNavClick(item.href) : (e) => e.preventDefault()}
                  className={`px-3 xl:px-4 py-2 text-sm font-medium tracking-wide flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors ${
                    scrolled
                      ? "text-foreground-800 hover:text-primary-600"
                      : "text-background-50 hover:text-accent-300"
                  }`}
                >
                  {item.label}
                  {item.children.length > 0 && (
                    <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-xs"></i>
                  )}
                </a>
                {item.children.length > 0 && openKey === item.key && (
                  <div className="absolute top-full left-0 min-w-[240px] bg-background-50 border border-background-200 rounded-md py-2 mt-1">
                    {item.children.map((c) => (
                      <a
                        key={c.label}
                        href={c.href}
                        onClick={handleNavClick(c.href)}
                        className="block px-4 py-2 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                      >
                        {c.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-1 md:gap-2">
            <LanguageSelector scrolled={scrolled} />
            <button
              onClick={() => setSearchOpen(true)}
              aria-label={t("nav.search")}
              className={`w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
                scrolled
                  ? "text-foreground-800 hover:bg-background-100"
                  : "text-background-50 hover:bg-background-50/10"
              }`}
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <a
              href="/contact"
              onClick={handleNavClick("/contact")}
              aria-label={t("nav.contact")}
              className={`hidden md:flex w-10 h-10 items-center justify-center rounded-full cursor-pointer transition-colors ${
                scrolled
                  ? "text-foreground-800 hover:bg-background-100"
                  : "text-background-50 hover:bg-background-50/10"
              }`}
            >
              <i className="ri-user-line text-lg"></i>
            </a>
            <button
              onClick={() => setMobileOpen(true)}
              aria-label={t("nav.menu")}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
                scrolled ? "text-foreground-800" : "text-background-50"
              }`}
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-background-950/60" onClick={() => setMobileOpen(false)}>
          <div
            className="absolute top-0 right-0 h-full w-[85%] max-w-sm bg-background-50 shadow-xl p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <span className="font-heading font-bold text-lg text-primary-700">{t("nav.menu")}</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-10 h-10 flex items-center justify-center cursor-pointer"
                aria-label={t("nav.close")}
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <nav className="space-y-1">
              {menuItems.map((item) => (
                <div key={item.key} className="border-b border-background-200 py-2">
                  <a
                    href={item.href}
                    onClick={handleNavClick(item.href)}
                    className="block py-2 text-base font-medium text-foreground-900 cursor-pointer"
                  >
                    {item.label}
                  </a>
                  {item.children.length > 0 && (
                    <div className="pl-4 pb-2 space-y-1">
                      {item.children.map((c) => (
                        <a
                          key={c.label}
                          href={c.href}
                          onClick={handleNavClick(c.href)}
                          className="block py-1.5 text-sm text-foreground-600 hover:text-primary-600 cursor-pointer"
                        >
                          {c.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Search modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-background-950/70 flex items-start justify-center pt-32 px-4" onClick={() => setSearchOpen(false)}>
          <div
            className="w-full max-w-2xl bg-background-50 rounded-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 border-b border-background-300 pb-3">
              <i className="ri-search-line text-xl text-foreground-500"></i>
              <input
                type="text"
                placeholder={t("nav.searchPlaceholder")}
                className="flex-1 outline-none bg-transparent text-base text-foreground-900 placeholder:text-foreground-400"
                autoFocus
              />
              <button
                onClick={() => setSearchOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-foreground-600 hover:text-primary-600 cursor-pointer"
                aria-label={t("nav.close")}
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="mt-4 text-xs text-foreground-500">{t("nav.hotSearch")}</div>
          </div>
        </div>
      )}
    </header>
  );
}